import * as React from 'react';
import { Sample, Campaign, CampaignFactory, Station, StationFactory } from '../../resource/';
import { IAuthProps, Query } from '../../services/';

import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';
import { Section } from '../../modules';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { TidalSituation } from '../Campaign/TidalSituation';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Label } from '@independent-software/typeui/controls/Label';

interface IProps {
  /** Initial form data. */
  data: Sample;
  /** Called whenever form changes. */
  onChange: (data: Sample, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

interface IState {
  /** Current form data. */
  data: Sample;
  
  campaigns: Campaign[];
  stations: Station[];
}

class SampleForm extends React.Component<IAuthProps & IProps, IState> {
  constructor(props: IAuthProps & IProps) {
    super(props);

    // Intialize state.
    this.state = {
      data: props.data,
      campaigns: [],
      stations: []
    };    
  }


  componentDidMount = () => {
    this.handleCampaignSearch();
    this.handleStationSearch();
  }

  handleCampaignSearch = (q?: string) => {
    // Retrieve a list of campaigns:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    CampaignFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ campaigns: res.items }));
  }

  handleStationSearch = (q?: string) => {
    // Retrieve a list of stations:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    StationFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ stations: res.items }));
  }

  renderCampaign = () => 
    <Form.Field
      required={{message: "Campaign is required."}}
      width={1} label="Campaign"
      name="campaign" 
      value={this.state.data.campaign}
      control={(<Dropdown onSearch={this.handleCampaignSearch} fluid data={this.state.campaigns} placeholder="Campaign" label={(item:Campaign) => item.name}>
        <Dropdown.Column weight={1}>{(item:Campaign) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a campaign."/>    

  renderStation = () => 
    <Form.Field
      required={{message: "Station is required."}}
      width={1} label="Station"
      name="station" 
      value={this.state.data.station}
      control={(<Dropdown onSearch={this.handleStationSearch} fluid data={this.state.stations} placeholder="Station" label={(item:Station) => item.name}>
        <Dropdown.Column weight={1}>{(item:Station) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a station."/>   

  renderDate = () =>
    <Form.Field
      name="date" label="Date"
      required={{message: "Sample data is required."}}
      value={this.state.data.date}
      control={(<Input type="date" nofuture placeholder="Date" fluid/>)}
      hint={(<React.Fragment>Please select a date for the sample.</React.Fragment>)}/>  

  renderTime = () =>
    <Form.Field
      name="time" label="Time"
      value={this.state.data.time}
      control={(<Input type="time" is24h hasSeconds clock placeholder="Time" fluid/>)}
      hint={(<React.Fragment>Please select a time for the sample.</React.Fragment>)}/>        

  renderTidalSituation = () =>
    <Form.Field 
      name="tidal_situation" label="Tidal situtation"
      value={this.state.data.tidal_situation}
      control={<Dropdown placeholder="Tidal situation" fluid data={TidalSituation.getList()}
        label={(item) => TidalSituation.toName(item)}>
        <Dropdown.Column>{(item) => TidalSituation.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select the tide condition.</React.Fragment>)}/>         
          
  renderDepthType = () =>      
    <Form.Field
      name="depth_type" label="Depth level"
      value={this.state.data.depth_type}
      control={(<Input type="text" placeholder="Depth level" fluid/>)}
      hint={(<React.Fragment>Please enter a sampling position (e.g. surface, mid-water, bottom).</React.Fragment>)}/>  

  renderDepth = () =>      
    <Form.Field
      name="depth" label="Depth"
      value={this.state.data.depth}
      control={(<Input type="text" placeholder="Depth" fluid><Label pointing="left" attached="right">m</Label></Input>)}
      required={{message: "Sample depth is required."}}
      isFloat={{message: 'Depth must be numeric.'}}
      min={{value: 0, message: 'Must be a positive number.'}}
      hint={(<React.Fragment>Please enter a depth from the surface at which sample is collected.</React.Fragment>)}/>  

  renderInterval = () =>      
    <Form.Field
      name="interval" label="Interval"
      value={this.state.data.interval}
      control={(<Input type="text" placeholder="Interval" fluid><Label pointing="left" attached="right">min</Label></Input>)}
      isFloat={{message: 'Interval must be numeric.'}}
      min={{value: 0, message: 'Must be a positive number.'}}
      hint={(<React.Fragment>Please enter a the time taken to collect the sample.</React.Fragment>)}/>    


  render() {
    let p = this.props;
    return (
      <Form
        data={this.state.data} dirty={p.dirty} onChange={p.onChange} onValidate={p.onValidate}>
        <Tabs underlined>
          <Tabs.Pane label="General">
            <Section padded>
              <Flex.Columns count={2}>
                {this.renderCampaign()}
                {this.renderStation()}
                {this.renderDate()}
                {this.renderDepth()}
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
          <Tabs.Pane label="Details">
            <Section padded>
              <Flex.Columns count={2}>
                {this.renderTime()}
                {this.renderInterval()}
                {this.renderTidalSituation()}
                {this.renderDepthType()}
              </Flex.Columns>
            </Section>
          </Tabs.Pane>
        </Tabs>
      </Form>
    )
  }
}

export { SampleForm };
